import { X, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useEffect } from 'react';
import { formatWeekLabel, getWeekStartISO } from '../utils/dateUtils';

const daysOfWeek = [
  { short: 'Sun', full: 'Sunday' },
  { short: 'Mon', full: 'Monday' },
  { short: 'Tue', full: 'Tuesday' },
  { short: 'Wed', full: 'Wednesday' },
  { short: 'Thu', full: 'Thursday' },
  { short: 'Fri', full: 'Friday' },
  { short: 'Sat', full: 'Saturday' },
];

export function AddToMealPlanDrawer({ isOpen, onClose, onConfirm, recipe, currentWeekIndex, weeksData, weekStartDow = 1, actualCurrentWeekIndex = 2 }) {
  const [selectedWeekIndex, setSelectedWeekIndex] = useState(currentWeekIndex);
  const [selectedDay, setSelectedDay] = useState(null);

  // Build day list rotated to user week start
  const orderedDays = (() => {
    const start = Math.min(Math.max(weekStartDow, 0), 6);
    return daysOfWeek.slice(start).concat(daysOfWeek.slice(0, start));
  })();

  // Reset selected week when drawer opens
  useEffect(() => {
    if (isOpen) {
      setSelectedWeekIndex(currentWeekIndex);
      setSelectedDay(null);
    }
  }, [isOpen, currentWeekIndex]);

  if (!isOpen) return null;

  const mealTypeLabel = recipe.mealType.charAt(0).toUpperCase() + recipe.mealType.slice(1);
  const selectedWeekStartISO = (() => {
    const weeksOffset = selectedWeekIndex - actualCurrentWeekIndex;
    const base = new Date();
    base.setDate(base.getDate() + weeksOffset * 7);
    return getWeekStartISO(base, weekStartDow);
  })();
  const selectedWeekLabel = formatWeekLabel(new Date(`${selectedWeekStartISO}T00:00:00Z`));

  const handlePreviousWeek = () => {
    if (selectedWeekIndex > 0) {
      setSelectedWeekIndex(selectedWeekIndex - 1);
    }
  };

  const handleNextWeek = () => {
    if (selectedWeekIndex < weeksData.length - 1) {
      setSelectedWeekIndex(selectedWeekIndex + 1);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed inset-x-0 bottom-0 bg-card text-foreground rounded-t-3xl z-50 max-w-md mx-auto animate-slide-up">
        {/* Handle bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 bg-muted rounded-full" />
        </div>

        <div className="px-6 pb-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl">Add to Meal Plan</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>

          {/* Recipe Info */}
          <div className="flex items-center gap-3 p-3 bg-muted rounded-xl mb-6">
            <img 
              src={recipe.image} 
              alt={recipe.name}
              loading="lazy"
              decoding="async"
              fetchPriority="low"
              className="w-16 h-16 rounded-lg object-cover"
            />
            <div className="flex-1">
              <h3 className="font-medium text-foreground">{recipe.name}</h3>
              <p className="text-sm text-muted-foreground">{mealTypeLabel}</p>
            </div>
          </div>

          {/* Week Info */}
          <div className="flex items-center gap-2 text-muted-foreground mb-4">
            <Calendar className="w-4 h-4" />
            <span className="text-sm">{selectedWeekLabel}</span>
          </div>

          {/* Week Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handlePreviousWeek}
              className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              <span className="text-sm">Previous Week</span>
            </button>
            <button
              onClick={handleNextWeek}
              className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
            >
              <span className="text-sm">Next Week</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Day Selection */}
          <div className="mb-6">
            <label className="block text-sm text-muted-foreground mb-3">Select day</label>
            <div className="grid grid-cols-7 gap-2">
              {orderedDays.map((day) => {
                const isSelected = selectedDay === day.short;
                return (
                  <button
                    key={day.short}
                    onClick={() => {
                      setSelectedDay(day.short);
                      onConfirm(selectedWeekIndex, day.short);
                    }}
                    className={`flex flex-col items-center gap-1 py-3 rounded-xl ${
                      isSelected ? 'day-select-selected' : 'day-select'
                    }`}
                  >
                    <span className="text-xs" style={isSelected ? { color: 'var(--accent-foreground)' } : { color: 'var(--foreground)' }}>
                      {day.short}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Cancel Button */}
          <button
            onClick={onClose}
            className="w-full py-3 border-2 border-[var(--border)] text-muted-foreground rounded-full hover:bg-muted transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}
